import { createApi } from '@reduxjs/toolkit/query/react';
import { backend_server_address } from '../../../../../../backendServer';
import { axiosBaseQuery } from '../../../../../../store/utility';
import { feedAPI, invalidateFeedEvents } from '../../../feed/ducks';
import { invalidateMaintenanceDashboardData } from '../../../analytics/ducks';
import type { FeedAuthor } from '../feed/feedTypes';
import type { AttachmentObjectKind, AttachmentsResponse } from '../attachments/attachmentTypes';

/** Identifies the object an attachment hangs off. `source_id` is not unique across kinds,
 * so the cache tag id is the composite `${objectKind}:${objectId}`. */
interface AttachmentArg {
    objectKind: AttachmentObjectKind;
    objectId: string;
}

const attachmentTag = ({ objectKind, objectId }: AttachmentArg) =>
    ({ type: 'Attachment', id: `${objectKind}:${objectId}` }) as const;

/**
 * Response shape of the backend `set_confirmed` endpoint
 * (`ImprovementViewSet.set_confirmed` → `{ improvement, confirmation }`).
 * `improvement` is serialized with `ImprovementSerializer`, so it exposes the
 * model's `id`/`is_confirmed`/`responsible` (NOT the feed item's shape).
 */
interface ConfirmImprovementResponse {
    improvement: {
        id: number;
        is_confirmed: boolean;
        responsible: FeedAuthor | null;
    };
}

/**
 * RTK Query API owned by the reusable event cards (e.g. WinCard's "confirm").
 * Lives with the cards (not in the throwaway insights/feed scaffolding) so the actions
 * travel with the components wherever they're used.
 *
 * `baseUrl` is `backend_server_address` (no `/api/`), so absolute backend paths like
 * `set_confirmed_url` ("/api/maintenance_module/.../set_confirmed/") are used as-is.
 */
export const cardEventsAPI = createApi({
    reducerPath: 'cardEventsApi',
    baseQuery: axiosBaseQuery({ baseUrl: backend_server_address }),
    tagTypes: ['Attachment'],
    endpoints: (builder) => ({
        confirmImprovement: builder.mutation<
            ConfirmImprovementResponse,
            { setConfirmedUrl: string; responsible: number | string; sourceId?: number }
        >({
            query: ({ setConfirmedUrl, responsible }) => ({
                url: setConfirmedUrl,
                method: 'POST',
                withCredentials: true,
                data: { responsible },
            }),
            // Patch the confirmed card into every active `feedAPI.getFeedByUrl` cache entry so
            // it re-renders green (`is_confirmed: true`) with the responsible name while the feed
            // refetches.
            // The endpoint returns the Improvement (not a feed item), so we match the card by the
            // `sourceId` we sent and graft only the fields the card reads.
            async onQueryStarted({ sourceId }, { dispatch, queryFulfilled, getState }) {
                try {
                    const { data } = await queryFulfilled;
                    const improvement = data?.improvement;
                    if (!improvement || sourceId == null) return;

                    const state: any = getState();
                    const feedQueries = state[feedAPI.reducerPath]?.queries ?? {};
                    Object.values(feedQueries).forEach((entry: any) => {
                        if (entry?.endpointName !== 'getFeedByUrl') return;
                        const feedUrl: string = entry.originalArgs;
                        dispatch(
                            feedAPI.util.updateQueryData('getFeedByUrl', feedUrl, (draft: any) => {
                                // Match on the composite identity: source_id is NOT unique across
                                // event types (a diagnostic/report can share it), and this feed is
                                // mixed ("diagnoses + linked wins"), so filter by type too.
                                const idx = draft.items?.findIndex(
                                    (i: any) => i.type === 'maintenance_improvement' && i.source_id === sourceId,
                                );
                                if (idx >= 0) {
                                    draft.items[idx] = {
                                        ...draft.items[idx],
                                        is_confirmed: improvement.is_confirmed ?? true,
                                        responsible: improvement.responsible ?? draft.items[idx].responsible,
                                    };
                                }
                            }),
                        );
                    });
                    // Refresh the lists themselves: both the new Feed module (Operation news +
                    // Asset history) and the legacy `getFeedByUrl` drawer provide the `Events` list
                    // tag. Confirming drops a win out of the pending-confirmation feed, which the
                    // patch above can't express.
                    dispatch(invalidateFeedEvents());
                    // And the wins panel: confirming leaves the chart's totals alone but moves the
                    // win out of the pending-confirmation count its shortcut button is labelled
                    // with, and out of the feed that button opens.
                    dispatch(invalidateMaintenanceDashboardData());
                } catch {
                    // Error surfaces via the mutation's `isError`/`error` state.
                }
            },
        }),
        // --- Attachments (observation / intervention / improvement) ---
        // `objectKind` is the URL segment and `objectId` the object PK (= feed `source_id`). We read
        // the attachments off the PARENT object (`GET /<kind>/<id>/`), whose `attachments` array
        // includes `file_name` and the file URL (unlike the heavy base64 `/attachments/` endpoint).
        // The card/manager read from `getAttachments` (fetched on expand), so upload/delete only need
        // to invalidate the shared `Attachment` tag. `baseUrl` has no `/api/`, so the path is built here.
        getAttachments: builder.query<AttachmentsResponse, AttachmentArg>({
            query: ({ objectKind, objectId }) => ({
                url: `/api/maintenance_module/${objectKind}/${objectId}/`,
                method: 'GET',
                withCredentials: true,
            }),
            providesTags: (_result, _error, arg) => [attachmentTag(arg)],
        }),
        uploadAttachment: builder.mutation<void, AttachmentArg & { file: File }>({
            query: ({ objectKind, objectId, file }) => {
                // axios detects FormData and sets the multipart boundary automatically.
                const data = new FormData();
                data.append('file', file);
                return {
                    url: `/api/maintenance_module/${objectKind}/${objectId}/upload_file/`,
                    method: 'POST',
                    withCredentials: true,
                    data,
                };
            },
            invalidatesTags: (_result, _error, arg) => [attachmentTag(arg)],
        }),
        deleteAttachment: builder.mutation<void, AttachmentArg & { fileId: string }>({
            query: ({ objectKind, objectId, fileId }) => ({
                url: `/api/maintenance_module/${objectKind}/${objectId}/remove_file/${fileId}/`,
                method: 'PATCH',
                withCredentials: true,
            }),
            invalidatesTags: (_result, _error, arg) => [attachmentTag(arg)],
        }),
    }),
});

export const {
    useConfirmImprovementMutation,
    useGetAttachmentsQuery,
    useUploadAttachmentMutation,
    useDeleteAttachmentMutation,
} = cardEventsAPI;
export const cardEventsAPIReducer = cardEventsAPI.reducer;
export const cardEventsAPIReducerPath = cardEventsAPI.reducerPath;
export const cardEventsAPIReducerMiddleware = cardEventsAPI.middleware;
